import * as THREE from 'three';
import { box, cyl, sphere } from '../core/meshkit.js';
import { C } from '../core/palette.js';

// A simple dungeon enemy: a lumpy dough-golem with a telegraphed melee attack.
// The scene drives combat by calling strike() / update(); this class just
// manages HP, state, and animation.

export class DungeonEnemy {
  constructor(type = 'golem'){
    this.type = type;
    this.maxHp = type === 'golem' ? 3 : (type === 'skeleton' ? 2 : (type === 'burnt' ? 8 : 5));
    this.hp = this.maxHp;
    this.damage = type === 'burnt' ? 2 : 1;
    this.dead = false;
    this.state = 'idle';        // idle | telegraph | attacking | recovery
    this.timer = 0;
    this.phase = Math.random() * Math.PI * 2;

    this.root = new THREE.Group();
    if (type === 'skeleton') this._buildSkeleton();
    else if (type === 'burnt') this._buildBurnt();
    else this._buildMesh();

    // invisible click target for sword swings
    this.hitTarget = new THREE.Mesh(
      new THREE.CylinderGeometry(0.8, 0.8, 2.2, 12),
      new THREE.MeshBasicMaterial({ visible: false })
    );
    this.hitTarget.position.set(0, 1.1, 0);
    this.hitTarget.userData.enemy = this;
    this.root.add(this.hitTarget);
  }

  _buildMesh(){
    const g = new THREE.Group();
    // lumpy body
    this.body = sphere(0.5, 0x8C7A62, 0, 0.85, 0, g, 14);
    this.body.scale.set(1.1, 1, 1);
    // crusty chunks
    sphere(0.22, 0x6E5E4E, 0.35, 0.95, 0.25, g, 8);
    sphere(0.18, 0x6E5E4E, -0.32, 1.05, -0.18, g, 8);
    sphere(0.2, 0x6E5E4E, 0.12, 0.55, 0.38, g, 8);
    // angry eyes
    sphere(0.08, 0xFFD27A, -0.18, 1.05, 0.35, g, 10);
    sphere(0.08, 0xFFD27A, 0.18, 1.05, 0.35, g, 10);
    sphere(0.04, 0x1A1A1A, -0.18, 1.06, 0.4, g, 8);
    sphere(0.04, 0x1A1A1A, 0.18, 1.06, 0.4, g, 8);
    // stubby arms
    cyl(0.12, 0.14, 0.55, 0x7C6A52, -0.55, 0.7, 0.15, g, 8).rotation.z = 0.6;
    cyl(0.12, 0.14, 0.55, 0x7C6A52, 0.55, 0.7, 0.15, g, 8).rotation.z = -0.6;
    // legs
    cyl(0.13, 0.11, 0.45, 0x7C6A52, -0.25, 0.22, 0, g, 8);
    cyl(0.13, 0.11, 0.45, 0x7C6A52, 0.25, 0.22, 0, g, 8);
    this.root.add(g);
    this.mesh = g;
  }

  _buildSkeleton(){
    const g = new THREE.Group();
    const bone = 0xE8E0D0;
    // skull
    this.body = sphere(0.34, bone, 0, 1.35, 0, g, 12);
    // eye sockets
    sphere(0.07, 0x1A1A1A, -0.12, 1.38, 0.24, g, 8);
    sphere(0.07, 0x1A1A1A,  0.12, 1.38, 0.24, g, 8);
    // ribcage
    cyl(0.05, 0.05, 0.55, bone, 0, 0.95, 0, g, 8);
    for (let i = 0; i < 4; i++){
      const rib = cyl(0.03, 0.03, 0.55, bone, 0, 0.82 + i*0.12, 0, g, 6);
      rib.rotation.z = Math.PI/2;
    }
    // arms
    cyl(0.04, 0.04, 0.55, bone, -0.35, 1.05, 0.1, g, 6).rotation.z = 0.5;
    cyl(0.04, 0.04, 0.55, bone,  0.35, 1.05, 0.1, g, 6).rotation.z = -0.5;
    // legs
    cyl(0.05, 0.04, 0.7, bone, -0.15, 0.35, 0, g, 6);
    cyl(0.05, 0.04, 0.7, bone,  0.15, 0.35, 0, g, 6);
    this.root.add(g);
    this.mesh = g;
  }

  _buildBurnt(){
    const g = new THREE.Group();
    const ash = 0x2A2420, ember = 0xFF6A2C;
    // charred loaf body
    this.body = sphere(0.65, ash, 0, 0.9, 0, g, 16);
    this.body.scale.set(1.2, 1, 1);
    // cracks with embers
    sphere(0.12, ember, -0.35, 1.05, 0.25, g, 8);
    sphere(0.09, ember,  0.32, 0.95, -0.18, g, 8);
    sphere(0.1,  ember, 0.12, 0.55, 0.38, g, 8);
    // angry glowing eyes
    sphere(0.1, ember, -0.22, 1.05, 0.42, g, 10);
    sphere(0.1, ember,  0.22, 1.05, 0.42, g, 10);
    sphere(0.05, 0x1A1A1A, -0.22, 1.06, 0.48, g, 8);
    sphere(0.05, 0x1A1A1A,  0.22, 1.06, 0.48, g, 8);
    // chunky arms
    cyl(0.18, 0.2, 0.7, ash, -0.65, 0.7, 0.15, g, 8).rotation.z = 0.55;
    cyl(0.18, 0.2, 0.7, ash,  0.65, 0.7, 0.15, g, 8).rotation.z = -0.55;
    // legs
    cyl(0.18, 0.14, 0.55, ash, -0.3, 0.27, 0, g, 8);
    cyl(0.18, 0.14, 0.55, ash,  0.3, 0.27, 0, g, 8);
    this.root.add(g);
    this.mesh = g;
  }

  // Player clicked on this enemy during combat.
  takeDamage(amount){
    if (this.dead) return false;
    this.hp -= amount;
    this._flash(0xE6537B);
    if (this.hp <= 0){
      this.dead = true;
      this.state = 'dead';
      this.mesh.rotation.x = -Math.PI / 2;
      this.mesh.position.y = 0.25;
      return true;
    }
    return false;
  }

  // The enemy is allowed to start a telegraphed attack.
  startAttack(){
    if (this.dead || this.state !== 'idle') return;
    this.state = 'telegraph';
    this.timer = 1.2;
    this._flash(0xFF9A3C);
  }

  // Called each frame by the dungeon scene.
  update(dt, breadPos){
    if (this.dead) return;
    this.phase += dt * 2;

    // idle bob
    if (this.state === 'idle'){
      this.root.position.y = Math.abs(Math.sin(this.phase)) * 0.06;
      this.mesh.rotation.y = Math.sin(this.phase * 0.5) * 0.1;
      return;
    }

    // telegraph: pulse and shrink timer
    if (this.state === 'telegraph'){
      this.timer -= dt;
      const s = 1 + Math.sin(this.timer * 12) * 0.12;
      this.body.scale.set(1.1 * s, 1 * s, 1.1 * s);
      if (this.timer <= 0){
        this.state = 'attacking';
        this.timer = 0.55; // active hurt window
        this._flash(0xFFD27A);
      }
      return;
    }

    // attacking window
    if (this.state === 'attacking'){
      this.timer -= dt;
      this.root.position.z += dt * 2.2; // lunge toward player
      if (this.timer <= 0){
        this.state = 'recovery';
        this.timer = 0.9;
      }
      return;
    }

    // recovery: slide back
    if (this.state === 'recovery'){
      this.timer -= dt;
      this.root.position.z -= dt * 1.4;
      if (this.timer <= 0){
        this.state = 'idle';
        this.root.position.z = 0;
      }
    }
  }

  _flash(color){
    if (!this.body.material) return;
    const old = this.body.material.color.getHex();
    this.body.material.color.setHex(color);
    setTimeout(() => {
      if (this.body && this.body.material) this.body.material.color.setHex(old);
    }, 120);
  }
}
